import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, LockKey, CheckCircle, WarningCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordInput, PasswordStrength } from "@/components/PasswordInput";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1631812314418-d2c27f4d85ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMHN1bnNldHxlbnwwfHx8fDE3NzY5ODg2NTR8MA&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [context, setContext] = useState({ email: "", name: "", purpose: "reset" });

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidationError("This link is missing a reset token.");
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/validate-reset-token", { token });
        setContext({ email: data.email, name: data.name, purpose: data.purpose });
      } catch (err) {
        setValidationError(formatApiErrorDetail(err.response?.data?.detail) || "This link is invalid or expired.");
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const isInvite = context.purpose === "invite";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(isInvite ? "Password set — welcome aboard!" : "Password updated — you can sign in now");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-ink text-white grain">
      <div className="absolute inset-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/90" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="p-8">
          <Logo size="default" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            {validating ? (
              <div className="flex flex-col items-center text-white/60 py-12" data-testid="reset-validating">
                <CircleNotch size={32} className="animate-spin text-[#D4AF37] mb-4" />
                <p className="text-[11px] uppercase tracking-[0.3em] font-sans">Verifying your link</p>
              </div>
            ) : validationError ? (
              <div data-testid="reset-error-state">
                <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-400/40 flex items-center justify-center mb-6">
                  <WarningCircle size={28} className="text-red-300" weight="duotone" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.35em] text-red-300 mb-4 font-sans">
                  Link Issue
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
                  This link <span className="italic text-[#D4AF37]">won't work</span>
                </h1>
                <p className="text-white/70 text-sm font-sans leading-relaxed mb-8">
                  {validationError}
                </p>
                <Link
                  to="/forgot-password"
                  data-testid="reset-request-new-link"
                  className="inline-block h-12 px-6 leading-[3rem] bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-sm uppercase tracking-[0.25em] transition-all"
                >
                  Request a new link
                </Link>
                <div className="mt-6">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#D4AF37] transition-colors font-sans"
                  >
                    <ArrowLeft size={14} />
                    Back to login
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-10">
                  <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-sans">
                    {isInvite ? "Welcome aboard" : "New password"}
                  </p>
                  <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
                    {isInvite ? (
                      <>Set your <span className="italic text-[#D4AF37]">password</span></>
                    ) : (
                      <>Choose a new <span className="italic text-[#D4AF37]">password</span></>
                    )}
                  </h1>
                  <p className="text-white/60 text-sm font-sans">
                    {isInvite
                      ? `Finish setting up your account for ${context.email}.`
                      : `Updating password for ${context.email}.`}
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                      New password
                    </Label>
                    <PasswordInput
                      icon={LockKey}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      data-testid="reset-new-password-input"
                    />
                    <PasswordStrength value={password} />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                      Confirm password
                    </Label>
                    <PasswordInput
                      icon={LockKey}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      placeholder="Repeat the password"
                      data-testid="reset-confirm-password-input"
                    />
                    {confirm && password && confirm !== password && (
                      <p className="text-[10px] text-red-300 mt-1.5 font-sans" data-testid="confirm-mismatch">
                        Passwords don't match.
                      </p>
                    )}
                    {confirm && password === confirm && (
                      <p className="text-[10px] text-emerald-300 mt-1.5 font-sans flex items-center gap-1" data-testid="confirm-match">
                        <CheckCircle size={11} weight="duotone" /> Passwords match
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !password || !confirm}
                    className="w-full h-12 bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-sm uppercase tracking-[0.25em] transition-all duration-200 hover:-translate-y-[1px]"
                    data-testid="reset-submit-btn"
                  >
                    {submitting ? (
                      <CircleNotch size={18} className="animate-spin" />
                    ) : isInvite ? (
                      <>
                        <CheckCircle size={16} className="mr-2" weight="duotone" />
                        Activate account
                      </>
                    ) : (
                      "Update password"
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="p-6 text-center text-[11px] uppercase tracking-[0.3em] text-white/30 font-sans">
          A curated concierge experience
        </div>
      </div>
    </div>
  );
}
